import { problemDetailsSchema, } from "@commerce-os/contracts";
/** The API answered with an error (problem+json, CLAUDE.md §9). Branch on `code`, never on `detail`. */
export class ApiError extends Error {
    status;
    code;
    detail;
    /** Field errors of a 400 `VALIDATION_FAILED`, for forms. */
    errors;
    /** Quote this when reporting a problem; absent when the error did not come from the API (e.g. a proxy). */
    requestId;
    /** The server's `Retry-After` (429/503), in milliseconds; absent when it sent none. */
    retryAfterMs;
    /** With `PLAN_LIMIT_REACHED` / `FEATURE_NOT_IN_PLAN`: the plan key, its limit and the usage. */
    entitlement;
    constructor(status, problem, retryAfterMs) {
        const code = problem?.code ?? fallbackCode(status);
        super(problem?.detail ?? problem?.title ?? `HTTP ${String(status)} ${code}`);
        this.name = "ApiError";
        this.status = status;
        this.code = code;
        this.detail = problem?.detail;
        this.errors = problem?.errors ?? [];
        this.requestId = problem?.requestId;
        this.retryAfterMs = retryAfterMs;
        this.entitlement = problem?.entitlement;
    }
}
/** The request never got an answer (offline, DNS, connection reset). Safe to retry with the same key. */
export class ApiNetworkError extends Error {
    constructor(cause) {
        super("The API could not be reached", { cause });
        this.name = "ApiNetworkError";
    }
}
/** A non-problem error body (e.g. an HTML 502 from a proxy) still gets a stable code. */
function fallbackCode(status) {
    if (status === 401)
        return "UNAUTHENTICATED";
    if (status === 403)
        return "FORBIDDEN";
    if (status === 404)
        return "NOT_FOUND";
    if (status === 429)
        return "RATE_LIMITED";
    if (status === 502 || status === 503 || status === 504)
        return "SERVICE_UNAVAILABLE";
    return "INTERNAL";
}
export async function apiErrorFrom(response) {
    let body;
    try {
        body = await response.json();
    }
    catch {
        body = undefined;
    }
    const problem = problemDetailsSchema.safeParse(body);
    return new ApiError(response.status, problem.success ? problem.data : undefined, retryAfter(response.headers.get("retry-after")));
}
/** `Retry-After` as delay-seconds or an HTTP date (RFC 9110 §10.2.3), in milliseconds from now. */
export function retryAfter(value, now = Date.now()) {
    if (value === null || value.trim() === "")
        return undefined;
    if (/^\d+$/.test(value.trim()))
        return Number(value.trim()) * 1_000;
    const at = Date.parse(value);
    return Number.isNaN(at) ? undefined : Math.max(0, at - now);
}
//# sourceMappingURL=errors.js.map